import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import EveryDoggyAPIServices from '../../utils/services/EveryDoggyAPIServices';
import CalculateOfferPrices from '../../utils/scripts/calculateOfferPrices';
import { setPaymentProduct } from '../../reduxStore/data/actions';
import { WebCatalogProduct } from '../../utils/services/EveryDoggyAPITypes';

/** Props shared by every offer variant in this paywall. */
export type OfferProps = {
  currentProduct: string | null;
  secondScreenWrapper?: boolean;
};

export type Prices = {
  firstOption?: WebCatalogProduct;
  secondOption?: WebCatalogProduct;
  thirdOption?: WebCatalogProduct;
};

/**
 * Structural type for a price config entry coming from
 * `getPricesConfig().everydoggyNewDesign` (the config itself is untyped).
 */
export interface OfferPriceConfig {
  id: number | string;
  defaultPricePerDay?: string;
  defaultPrices?: string;
  defaultPricesPerWeek?: string;
  defaultOldPrices?: string;
  defaultOldPricesForPeriod?: string;
  productName?: string;
  titleWeek?: string;
  perDay?: string;
}

/** Loads the three catalog products for the current tariff's price configs. */
export const useOfferProducts = (
  firstPriceConfig: OfferPriceConfig,
  secondPriceConfig: OfferPriceConfig,
  thirdPriceConfig: OfferPriceConfig,
  tariff: string
): Prices => {
  const [prices, setPrices] = useState<Prices>({});

  useEffect(() => {
    async function fetchData() {
      const [firstOption, secondOption, thirdOption] = await Promise.all([
        EveryDoggyAPIServices.getWebCatalogProduct(firstPriceConfig.id.toString()),
        EveryDoggyAPIServices.getWebCatalogProduct(secondPriceConfig.id.toString()),
        EveryDoggyAPIServices.getWebCatalogProduct(thirdPriceConfig.id.toString()),
      ]);
      setPrices({ firstOption, secondOption, thirdOption });
    }
    fetchData();
  }, [tariff]);

  return prices;
};

/** Loads a single catalog product — for single-price offer variants (v001/v002). */
export const useSingleOfferProduct = (priceConfig: OfferPriceConfig, tariff: string): Prices => {
  const [prices, setPrices] = useState<Prices>({});

  useEffect(() => {
    async function fetchData() {
      const firstOption = await EveryDoggyAPIServices.getWebCatalogProduct(priceConfig.id.toString());
      setPrices({ firstOption });
    }
    fetchData();
  }, [tariff]);

  return prices;
};

/** Per-day trial prices for the three options — identical math in every offer variant. */
export const useDayPrices = (
  prices: Prices,
  firstPriceConfig: OfferPriceConfig,
  secondPriceConfig: OfferPriceConfig,
  thirdPriceConfig: OfferPriceConfig
) => {
  const firstPriceInDay = useMemo(
    () =>
      CalculateOfferPrices.setPriceOneDaytrial({
        product: prices.firstOption,
        defaultPrice: firstPriceConfig.defaultPricePerDay,
        calculateFromIntro: true,
      }),
    [prices.firstOption, firstPriceConfig]
  );

  const secondPriceInDay = useMemo(
    () =>
      CalculateOfferPrices.setPriceOneDaytrial({
        product: prices.secondOption,
        defaultPrice: secondPriceConfig.defaultPricePerDay,
        calculateFromIntro: true,
      }),
    [prices.secondOption, secondPriceConfig]
  );

  const thirdPriceInDay = useMemo(
    () =>
      CalculateOfferPrices.setPriceOneDaytrial({
        product: prices.thirdOption,
        defaultPrice: thirdPriceConfig.defaultPricePerDay,
        calculateFromIntro: true,
      }),
    [prices.thirdOption, thirdPriceConfig]
  );

  return { firstPriceInDay, secondPriceInDay, thirdPriceInDay };
};

export const priceFirstPart = (price: number | string): number => Math.trunc(Number(price));

export const priceSecondPart = (price: number | string | undefined): string => {
  if (price) {
    const decimalPart = price.toString().split('.')[1];
    return decimalPart ? decimalPart.padEnd(2, '0') : '00';
  }
  return '';
};

/** "1-week plan" → "1-Week Plan" — option titles come lowercased from the config. */
export const formatOptionTitle = (title = ''): string =>
  title
    .toLowerCase()
    .replace(/-\w/g, match => match.toUpperCase())
    .replace(/^\w/, match => match.toUpperCase());

/** Rounding quirk kept from the legacy code: a 0.28/day price renders as 0.29. */
export const adjustDayPrice = (price: number): number | string => (price === 0.28 ? '0.29' : price);

/** Click handler that selects a pricing option (dispatches the payment product). */
export const useSelectProduct = () => {
  const dispatch = useDispatch();

  return (
    optionValue: string | number | undefined,
    productName: string | undefined,
    oldPrice: string | undefined
  ): void => {
    if (optionValue) {
      dispatch(
        setPaymentProduct(
          JSON.stringify({
            product: optionValue,
            oldPrice,
            name: productName,
          })
        )
      );
    }
  };
};
